/**
 * Paramètres > Licence : ligne « Renouvellement automatique » (état en français simple, jamais par la
 * seule couleur) et import d'un fichier de licence (.nxlic) pour un poste sans Internet.
 * Classes et couleurs existantes uniquement ; aucune modification de la charte graphique.
 */
import type { RenewalDescription } from '@infra/licence/LicenceRenewalStatus';
import { esc } from '@presentation/shell/format';

/** Taille maximale d'un fichier de licence (identique à la porte d'activation). */
export const LICENCE_FILE_MAX_BYTES = 64 * 1024;

const LEVEL_LABEL: Record<RenewalDescription['level'], string> = {
  info: 'Tout va bien',
  attention: 'À surveiller',
  blocage: 'Action nécessaire',
};

/** Ligne HTML du renouvellement automatique : un libellé d'état + la phrase explicative. */
export function renewalLineHtml(renewal: RenewalDescription): string {
  return (
    `<span class="licence-renewal licence-renewal--${renewal.level}" role="status">` +
    `<strong>Renouvellement automatique :</strong> ` +
    `<span class="licence-renewal-tag">${esc(LEVEL_LABEL[renewal.level])}</span> ` +
    `<span class="licence-renewal-text">${esc(renewal.text)}</span></span>`
  );
}

export type LicenceFileMessageKind = 'info' | 'success' | 'error';

/** Affiche un message sous le champ de fichier (zone annoncée aux lecteurs d'écran). */
export function setLicenceFileMessage(
  root: ParentNode,
  kind: LicenceFileMessageKind,
  text: string,
): void {
  const el = root.querySelector<HTMLElement>('#licence-file-msg');
  if (!el) return;
  el.textContent = text;
  el.className = `licence-file-msg licence-file-msg--${kind}`;
  el.setAttribute('role', kind === 'error' ? 'alert' : 'status');
}

/** Ouvre le déroulant « Poste sans Internet » quand l'utilisateur en a probablement besoin. */
export function openOfflineImportIfNeeded(
  root: ParentNode,
  level: RenewalDescription['level'],
): void {
  const details = root.querySelector<HTMLDetailsElement>('#licence-offline-details');
  if (details && level !== 'info') details.open = true;
}

export interface LicenceFileImportDeps {
  importText: (text: string) => Promise<{ ok: true } | { ok: false; message: string }>;
  /** Recharge la licence et l'écran après un import réussi. */
  afterImport: () => Promise<void>;
  notify: {
    success: (message: string) => void;
    warning: (message: string) => void;
    error: (message: string) => void;
  };
}

/** Importe le fichier choisi : vérifie la taille, affiche chaque issue clairement, ne lève jamais. */
export async function runLicenceFileImport(
  root: ParentNode,
  deps: LicenceFileImportDeps,
): Promise<void> {
  const input = root.querySelector<HTMLInputElement>('#licence-file-input');
  const button = root.querySelector<HTMLButtonElement>('#licence-file-import');
  const file = input?.files?.[0];
  if (!file) {
    const message = 'Choisissez d’abord le fichier de licence (.nxlic).';
    setLicenceFileMessage(root, 'error', message);
    deps.notify.warning(message);
    return;
  }
  if (file.size > LICENCE_FILE_MAX_BYTES) {
    const message = 'Ce fichier est trop volumineux pour être un fichier de licence.';
    setLicenceFileMessage(root, 'error', message);
    deps.notify.error(message);
    return;
  }
  button?.setAttribute('disabled', 'true');
  setLicenceFileMessage(root, 'info', 'Vérification du fichier en cours…');
  try {
    const result = await deps.importText(await file.text());
    if (!result.ok) {
      setLicenceFileMessage(root, 'error', result.message);
      deps.notify.error(result.message);
      return;
    }
    await deps.afterImport();
    if (input) input.value = '';
    const done = 'Licence mise à jour depuis le fichier : l’échéance est prolongée.';
    setLicenceFileMessage(root, 'success', done);
    deps.notify.success(done);
  } catch (error) {
    const message = error instanceof Error ? error.message : 'Import impossible.';
    setLicenceFileMessage(root, 'error', message);
    deps.notify.error(message);
  } finally {
    button?.removeAttribute('disabled');
  }
}
